import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInsightsCtx } from "./InsightsLayout";
import UnitEventsTable from "./unitEventsTable";
import Spinner from "../../components/ui/Spinner";
import ErrorState from "../../components/ui/ErrorState";

const SCROLL_KEY_PREFIX = "sensors-scroll:";

export default function SensorsPage() {
  const { buildingId = "" } = useParams();
  const navigate = useNavigate();

  // Restore scroll on mount; save it whenever the user leaves this page.
  useEffect(() => {
    const key = `${SCROLL_KEY_PREFIX}${buildingId}`;
    const raw = sessionStorage.getItem(key);
    const target = raw ? parseInt(raw, 10) : NaN;

    let cancelled = false;
    if (Number.isFinite(target) && target > 0) {
      // On a cold load the sensor table renders after its data query
      // resolves, so the document is short and scrollTo would clamp to the
      // current max height. Retry each frame until the page can reach the
      // saved offset (or we give up after ~1s).
      const start = performance.now();
      const tryScroll = () => {
        if (cancelled) return;
        const maxY =
          document.documentElement.scrollHeight - window.innerHeight;
        if (maxY >= target) {
          window.scrollTo(0, target);
          return;
        }
        if (performance.now() - start > 1000) {
          window.scrollTo(0, Math.max(0, maxY));
          return;
        }
        requestAnimationFrame(tryScroll);
      };
      requestAnimationFrame(tryScroll);
    }

    return () => {
      cancelled = true;
      try {
        sessionStorage.setItem(key, String(window.scrollY));
      } catch {
        /* storage unavailable */
      }
    };
  }, [buildingId]);
  const { buildings, buildingsLoading, startDate, endDate, searchQuery } =
    useInsightsCtx();

  if (buildingsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  const building = buildings.find((b) => b.buildingId === buildingId);
  if (!building) {
    return (
      <ErrorState
        title="Property not found"
        message="We couldn't find a property matching this URL."
        onRetry={() => navigate("/insights")}
      />
    );
  }

  return (
    <>
      <div className="flex justify-end px-4 pt-4">
        <button
          type="button"
          onClick={() => navigate(`/insights/properties/${buildingId}/report`)}
          className="rounded bg-primary px-4 py-2 text-sm font-semibold text-black"
        >
          Weekly report
        </button>
      </div>
      <UnitEventsTable
        building={building}
        startDate={startDate}
        endDate={endDate}
        searchQuery={searchQuery}
        onSensorClick={(sensor: { name: string }) =>
          navigate(
            `/insights/properties/${buildingId}/sensors/${encodeURIComponent(
              sensor.name
            )}`
          )
        }
      />
    </>
  );
}
